import axios, { AxiosError, isAxiosError } from "axios";
import type { LoginUser } from "../types/auth";
import { Environment } from "../types/environment";
import { SignInUrl } from "../utils/urls";
import { getLocalStorage, setLocalStorage } from "djuno-design";

export const PublicErrorMessage =
  "There was a problem receiving information. Try again later";

export const BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_BACK_END_URL + "v1"
    : "/v1";

export const S3_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_S3_BACK_END_URL + "simple-storage"
    : "/simple-storage";

export const BLOCK_EVENT_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_BLOCK_EVENT_BACK_END_URL + "event-api/v1"
    : "/event-api/v1";

export const DBS_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_DBS_BACK_END_URL + "databases"
    : "/databases";

export const WF_BASE_URL = "/wf";

export const INSTANCES_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_DBS_BACK_END_URL + "instance-services"
    : "/instance-services";

export const DAPP_BASE_URL = "/dapp-studio/management";

export const AI_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_DJUNO_AI_BACK_END_URL + "/djunoai"
    : "/djunoai";

export const K8_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_DBS_BACK_END_URL + "kubernetes"
    : "/kubernetes";

export const AI_LAB_BASE_URL =
  import.meta.env.VITE_MODE === "develop"
    ? import.meta.env.VITE_DBS_BACK_END_URL + "ai"
    : "/ai";

export const Axios = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const jwtAxios = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

type ApiScope =
  | "s3"
  | "event"
  | "dbs"
  | "wf"
  | "instances"
  | "dapp"
  | "djunoai"
  | "k8"
  | "ai-lab";

export const jwtEnvAxios = (
  scope: ApiScope | undefined = undefined,
  withoutEnv: boolean = false
) => {
  const selectedEnvId = getLocalStorage<Environment | null>("env", null)?.Id;

  let BaseUrl = BASE_URL;

  switch (scope) {
    case "s3":
      if (withoutEnv) {
        BaseUrl = S3_BASE_URL;
      } else {
        BaseUrl = S3_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    case "event":
      if (withoutEnv) {
        BaseUrl = BLOCK_EVENT_BASE_URL;
      } else {
        BaseUrl = BLOCK_EVENT_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    case "dbs":
      if (withoutEnv) {
        BaseUrl = DBS_BASE_URL;
      } else {
        BaseUrl = DBS_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    case "wf":
      if (withoutEnv) {
        BaseUrl = BaseUrl + WF_BASE_URL;
      } else {
        BaseUrl = BaseUrl + "/environments/" + selectedEnvId + WF_BASE_URL;
      }

      break;

    case "instances":
      if (withoutEnv) {
        BaseUrl = INSTANCES_BASE_URL;
      } else {
        BaseUrl = INSTANCES_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    case "dapp":
      if (withoutEnv) {
        BaseUrl = BaseUrl + DAPP_BASE_URL;
      } else {
        if (import.meta.env.VITE_USE_LOCAL_DAPP === "true") {
          BaseUrl = "http://localhost:4000";
        } else {
          BaseUrl = BaseUrl + "/environments/" + selectedEnvId + DAPP_BASE_URL;
        }
      }
      break;

    case "djunoai":
      BaseUrl = AI_BASE_URL;
      break;

    case "k8":
      if (withoutEnv) {
        BaseUrl = K8_BASE_URL;
      } else {
        BaseUrl = K8_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    case "ai-lab":
      if (withoutEnv) {
        BaseUrl = AI_LAB_BASE_URL;
      } else {
        BaseUrl = AI_LAB_BASE_URL + "/environments/" + selectedEnvId;
      }
      break;

    default:
      if (withoutEnv) {
        //
      } else {
        BaseUrl = BaseUrl + "/environments/" + selectedEnvId;
      }
      break;
  }

  const user = getLocalStorage<LoginUser | null>("login", null);
  const newAxios = axios.create({
    baseURL: BaseUrl,
    // withCredentials: true,
    headers: {
      Authorization: `Bearer ${user?.JwtToken}`,
      // "Content-Type": "application/json",
      Accept: "*/*",
    },
  });

  // newAxios.interceptors.request.use((config) => {
  //   let user = getLocalStorage<LoginUser | null>("login", null);
  //   config.headers.Authorization = `Bearer ${user?.JwtToken}`;
  //   return config;
  // });

  newAxios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (error.response.status === 401) {
        setLocalStorage("isAuthenticate", false);
        setLocalStorage("login", null);
        setLocalStorage("user", null);
        window.location.href = SignInUrl;
      } else {
        return Promise.reject(error);
      }
    }
  );

  return newAxios;
};

jwtAxios.interceptors.request.use((config) => {
  const user = getLocalStorage<LoginUser | null>("login", null);
  config.headers.Authorization = `Bearer ${user?.JwtToken}`;
  return config;
});

jwtAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response.status === 401) {
      setLocalStorage("isAuthenticate", false);
      setLocalStorage("login", null);
      setLocalStorage("user", null);

      if (window && window.Intercom) {
        window.Intercom("shutdown");
      }

      window.location.href = SignInUrl;
    } else {
      return Promise.reject(error);
    }
  }
);

export const FetchCatchResponse = (e: any) => {
  let message = "network error";
  let errors = [];
  let statusCode: number | undefined;
  if (e instanceof AxiosError) {
    console.log("ERROR", e);
    message = e.response?.data.message || e.response?.data.title || e.message;
    statusCode = e.response?.status;
    errors = e.response?.data.errors;
  } else {
    console.log("ERROR", e);
  }
  return {
    data: null,
    status: false,
    message,
    errors,
    statusCode,
  };
};

export const getExtractErrors = (e: AxiosError<any, any> | unknown) => {
  // console.log("isAxiosError(e)", isAxiosError(e));
  if (isAxiosError(e)) {
    let message = "";

    if (e.response?.data?.status === 400) {
      message = `<ul>${Object.values(e.response.data.errors).map(
        (e: any) => `<li>${e}</li>`
      )}</ul>`;
    } else {
      message = `<ul><li>${
        e.response?.data.Message ||
        e.response?.data.message ||
        `${e.response?.status}: ${e.response?.statusText}` ||
        PublicErrorMessage
      }</li></ul>`;
    }
    return message;
  } else {
    // console.log(e && 'message' in e ? e.message)
    // return "Bad Request";
  }
};

export const getExtractErrorCode = (e: AxiosError<any, any> | unknown) => {
  if (isAxiosError(e)) {
    return e.response?.status;
  } else {
    return 500;
  }
};
export default jwtAxios;
